import type { ModuleKey } from "./palettes.generated";

export type { ModuleKey };

/**
 * Route → clinical module, for the departmental theme.
 *
 * **Order matters and the list is not alphabetical.** First match wins, so the more specific rule
 * has to come first: nine radiology routes live *underneath* `/diagnostics`, and testing `lab`
 * first would swallow all of them.
 *
 * The organising principle, decided with stakeholders: the accent answers **"what kind of work is
 * this screen"**, not "which patient journey am I in". That is why `/sales/invoices` — labelled
 * "OP Billing" in the sidebar — is billing-orange rather than OP-blue, and why `/sales/ip-billing`
 * is orange too rather than IP-purple. Both are billing screens.
 *
 * Pharmacy owns the whole supply chain (inventory, procurement, warehousing, transport, drug master
 * data), because in a hospital that is who the supply chain exists for. IP therefore covers the
 * in-patient clinical screens under `/hospital` — ward issue, OT consumption, CSSD, surgical kits.
 */
const RULES: readonly (readonly [RegExp, ModuleKey])[] = [
  // Radiology sits inside /diagnostics, so it must be tested before lab.
  [/^\/diagnostics\/(radiology|pacs)/, "radiology"],
  [/^\/diagnostics/, "lab"],

  [/^\/hospital/, "ip"],

  [/^\/accounting|^\/pricing/, "billing"],
  [/^\/sales\/(invoices|ip-billing|consultants)/, "billing"],

  // Schedule H1 is a pharmacy register despite living under /sales.
  [/^\/sales\/(prescription-queue|schedule-register)/, "pharmacy"],
  [/^\/inventory|^\/procurement|^\/warehouse|^\/transport/, "pharmacy"],
  // Drug master data only. The rest of /catalog is cross-cutting and falls through.
  [/^\/catalog\/(items|generic-names|lasa|supply-types|item-)/, "pharmacy"],

  [/^\/admin|^\/reports|^\/integration/, "administration"],
] as const;

/**
 * Resolves the module whose accent the given path should wear.
 *
 * Falls back to `"op"` — which is today's blue — so the dashboard, the catalog and anything not yet
 * mapped keep looking exactly as they do now. An unmapped screen should read as "ordinary", not as
 * a colour someone forgot to assign.
 */
export function resolveModule(pathname: string): ModuleKey {
  for (const [pattern, key] of RULES) {
    if (pattern.test(pathname)) return key;
  }
  return "op";
}

/** Human labels, for the theme picker and anywhere the accent needs explaining. */
export const MODULE_LABELS: Record<ModuleKey, string> = {
  billing: "Billing",
  pharmacy: "Pharmacy",
  radiology: "Radiology",
  administration: "Administration",
  op: "OP",
  lab: "Lab",
  ip: "IP",
};
